/**
 * Portal shop — search, sort, category pills and product cards with add to
 * cart. Mounted by the after-me/portal-shop block.
 *
 * Products: GET ai-zippy/v1/products, one page at a time with Prev / Next. The
 * current page is kept in `?pg=` so a refresh or coming back from a product
 * lands on the same page. Pills: GET ai-zippy/v1/filter-options,
 * kept to the top-level categories the block lists in `categoryOrder`, in
 * that order. Add to cart goes through the Store API so the cart badge and
 * the cart page share one session.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { isAbort, restFetch, storeFetch } from "../portal/api.js";
import { decodeEntities } from "../portal/format.js";
import { emitCart, mountApp, QtyStepper } from "../portal/react.jsx";

const SORTS = {
	latest: { label: "Sort by: Latest", orderby: "date", order: "DESC" },
	name: { label: "Sort by: Name", orderby: "title", order: "ASC" },
	stock: { label: "Sort by: Stock", orderby: "stock", order: "DESC" },
};

const SEARCH_DELAY = 300;

// `page` / `paged` belong to WordPress, so the shop uses its own query arg.
const PAGE_PARAM = "pg";

function readPage() {
	const n = parseInt(new URLSearchParams(window.location.search).get(PAGE_PARAM), 10);
	return n > 1 ? n : 1;
}

function writePage(n) {
	const url = new URL(window.location.href);
	if (n > 1) url.searchParams.set(PAGE_PARAM, String(n));
	else url.searchParams.delete(PAGE_PARAM);
	window.history.replaceState(window.history.state, "", url);
}

// Bring the toolbar back into view after a page change, if it scrolled away.
function scrollToTop(el) {
	if (!el || el.getBoundingClientRect().top >= 0) return;
	const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
	el.scrollIntoView({ behavior: reduce ? "auto" : "smooth", block: "start" });
}

function useDebounced(value, delay) {
	const [debounced, setDebounced] = useState(value);
	useEffect(() => {
		const timer = setTimeout(() => setDebounced(value), delay);
		return () => clearTimeout(timer);
	}, [value, delay]);
	return debounced;
}

function SearchIcon() {
	return (
		<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
			<circle cx="11" cy="11" r="7" />
			<path d="M20 20l-3.5-3.5" />
		</svg>
	);
}

// Cards for anything but simple products link through to the product page.
const OPEN_LABELS = {
	variable: "Choose options",
	grouped: "View products",
	external: "View product",
};

function ProductCard({ product, lowThreshold }) {
	const managed = typeof product.stock_quantity === "number";
	const out = product.stock_status === "outofstock" || (managed && product.stock_quantity <= 0);
	const max = managed ? Math.max(1, product.stock_quantity) : 999;
	const low = managed && !out && product.stock_quantity < lowThreshold;
	const canAdd = product.type === "simple" && !out;

	const [qty, setQty] = useState(1);
	const [state, setState] = useState("idle"); // idle | busy | added
	const [error, setError] = useState("");

	const add = async () => {
		setState("busy");
		setError("");
		try {
			const cart = await storeFetch("wc/store/v1/cart/add-item", {
				method: "POST",
				body: { id: product.id, quantity: qty },
			});
			emitCart(cart);
			setQty(1);
			setState("added");
			setTimeout(() => setState("idle"), 1600);
		} catch (err) {
			setError(err.message);
			setState("idle");
		}
	};

	let stockText = "In stock";
	if (out) stockText = "Out of stock";
	else if (managed) stockText = `In stock: ${product.stock_quantity}`;

	const name = decodeEntities(product.name);

	return (
		<article className={`amp-card amp-product-card${out ? " is-out" : ""}`}>
			<a className="amp-product-card__link" href={product.permalink}>
				<div className="amp-product-card__media">
					{product.image ? (
						<img src={product.image} alt="" loading="lazy" />
					) : (
						<span className="amp-product-card__placeholder" />
					)}
				</div>
				<span className="amp-product-card__name">{name}</span>
			</a>
			{product.pack_size && <span className="amp-product-card__pack">{product.pack_size}</span>}
			<span className={`amp-product-card__stock${low ? " is-low" : ""}${out ? " is-out" : ""}`}>
				{stockText}
			</span>

			<div className="amp-product-card__actions">
				{canAdd ? (
					<>
						<QtyStepper
							value={qty}
							min={1}
							max={max}
							onChange={setQty}
							label={`Quantity of ${name}`}
							disabled={state === "busy"}
						/>
						<button
							type="button"
							className="amp-btn amp-btn--block"
							onClick={add}
							disabled={state === "busy"}
						>
							{state === "busy" ? "Adding…" : state === "added" ? "Added ✓" : "Add to Cart"}
						</button>
					</>
				) : out ? (
					<button type="button" className="amp-btn amp-btn--block" disabled>
						Out of stock
					</button>
				) : (
					<a className="amp-btn amp-btn--outline amp-btn--block" href={product.permalink}>
						{OPEN_LABELS[product.type] || "View options"}
					</a>
				)}
				{error && (
					<p className="amp-status amp-status--error" role="alert">
						{error}
					</p>
				)}
			</div>
		</article>
	);
}

function ShopApp({ config }) {
	const perPage = config.perPage || 24;
	const lowThreshold = Number(config.lowThreshold) || 0;

	const [search, setSearch] = useState("");
	const query = useDebounced(search.trim(), SEARCH_DELAY);
	const [sort, setSort] = useState("latest");
	const [category, setCategory] = useState(config.category || "");
	const [categories, setCategories] = useState([]);

	const [products, setProducts] = useState([]);
	const [page, setPage] = useState(1);
	const [pages, setPages] = useState(0);
	const [total, setTotal] = useState(0);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState("");

	const controller = useRef(null);
	const startPage = useRef(readPage());
	const topRef = useRef(null);

	// Pills, in the admin's category order.
	useEffect(() => {
		const order = Array.isArray(config.categoryOrder) ? config.categoryOrder : [];
		restFetch("ai-zippy/v1/filter-options")
			.then((data) => {
				const list = (data?.categories || []).filter((c) => order.includes(c.slug));
				list.sort((a, b) => order.indexOf(a.slug) - order.indexOf(b.slug));
				setCategories(list);
			})
			.catch(() => setCategories([]));
	}, [config.categoryOrder]);

	// Resolves true once the page is on screen, false on error or abort.
	const load = useCallback(
		async (nextPage) => {
			controller.current?.abort();
			const abort = new AbortController();
			controller.current = abort;

			const fetchPage = (n) =>
				restFetch("ai-zippy/v1/products", {
					signal: abort.signal,
					query: {
						search: query,
						category,
						orderby: SORTS[sort].orderby,
						order: SORTS[sort].order,
						page: n,
						per_page: perPage,
					},
				});

			setLoading(true);
			setError("");
			try {
				let target = nextPage;
				let data = await fetchPage(target);

				// A stale ?pg= past the last page falls back to the last page.
				if (target > 1 && data?.pages > 0 && target > data.pages) {
					target = data.pages;
					data = await fetchPage(target);
				}

				setProducts(data?.products || []);
				setPage(target);
				setPages(data?.pages || 0);
				setTotal(data?.total || 0);
				writePage(target);
				return true;
			} catch (err) {
				if (!isAbort(err)) setError(err.message);
				return false;
			} finally {
				if (controller.current === abort) setLoading(false);
			}
		},
		[query, category, sort, perPage]
	);

	// First load honours ?pg=; a new search, sort or category starts at page 1.
	useEffect(() => {
		load(startPage.current);
		startPage.current = 1;
	}, [load]);

	const goTo = (n) => {
		load(n).then((ok) => ok && scrollToTop(topRef.current));
	};

	const empty = !loading && !error && products.length === 0;
	const from = (page - 1) * perPage + 1;
	const to = from + products.length - 1;

	return (
		<>
			<div className="amp-toolbar" ref={topRef}>
				<label className="amp-search">
					<SearchIcon />
					<span className="screen-reader-text">Search products</span>
					<input
						type="search"
						placeholder="Search products…"
						value={search}
						onChange={(e) => setSearch(e.target.value)}
					/>
				</label>
				<select
					className="amp-select"
					aria-label="Sort products"
					value={sort}
					onChange={(e) => setSort(e.target.value)}
				>
					{Object.entries(SORTS).map(([key, { label }]) => (
						<option key={key} value={key}>
							{label}
						</option>
					))}
				</select>
			</div>

			{categories.length > 0 && (
				<div className="amp-pills" role="group" aria-label="Filter by category">
					<button
						type="button"
						className={`amp-pill${category === "" ? " is-active" : ""}`}
						aria-pressed={category === ""}
						onClick={() => setCategory("")}
					>
						All
					</button>
					{categories.map((cat) => (
						<button
							key={cat.slug}
							type="button"
							className={`amp-pill${category === cat.slug ? " is-active" : ""}`}
							aria-pressed={category === cat.slug}
							onClick={() => setCategory(cat.slug)}
						>
							{decodeEntities(cat.name)}
						</button>
					))}
				</div>
			)}

			{error && (
				<p className="amp-notice amp-notice--error" role="alert">
					{error}{" "}
					<button type="button" className="amp-link" onClick={() => load(page)}>
						Try again
					</button>
				</p>
			)}

			{empty && (
				<p className="amp-empty">
					{query ? `No products match “${query}”.` : "No products in this category yet."}
				</p>
			)}

			{products.length > 0 && (
				<div className="amp-grid" aria-busy={loading}>
					{products.map((product) => (
						<ProductCard key={product.id} product={product} lowThreshold={lowThreshold} />
					))}
				</div>
			)}

			{loading && products.length === 0 && (
				<div className="amp-grid" aria-hidden="true">
					{Array.from({ length: 8 }, (_, i) => (
						<div key={i} className="amp-product-card amp-product-card--skeleton amp-skeleton" />
					))}
				</div>
			)}

			{products.length > 0 && (
				<nav className="amp-pagination" aria-label="Product pages">
					<span className="amp-shop__count" aria-live="polite">
						Showing {from}–{to} of {total}
					</span>

					{pages > 1 && (
						<div className="amp-pagination__controls">
							<button
								type="button"
								className="amp-btn amp-btn--outline amp-btn--sm"
								onClick={() => goTo(page - 1)}
								disabled={loading || page <= 1}
							>
								<span className="amp-arrow amp-arrow--back" aria-hidden="true">
									←
								</span>
								Prev
							</button>
							<span className="amp-pagination__status">
								Page {page} of {pages}
							</span>
							<button
								type="button"
								className="amp-btn amp-btn--outline amp-btn--sm"
								onClick={() => goTo(page + 1)}
								disabled={loading || page >= pages}
							>
								Next
								<span className="amp-arrow" aria-hidden="true">
									→
								</span>
							</button>
						</div>
					)}
				</nav>
			)}
		</>
	);
}

mountApp("portal-shop", ShopApp);
